import React, { useState } from 'react';
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  HourglassOutlined,
  SyncOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import './JobsDetailsSidebar.scss';
import { Tooltip } from 'antd';
import { JobStatus } from 'components/StatusTag/StatusTag';
import { MVLanguageEnum } from 'types/types';
export interface JobDetailsSidebarProps {
  jobId: string | null;
  clusterName: string;
  onClose: () => void;
}

interface StatusConfig {
  label: string;
  color: string;
  bg: string;
  icon: React.ReactNode;
}

export const STATUS_CONFIG: Record<JobStatus, StatusConfig> = {
  Running: {
    label: 'Running',
    color: '#3b82f6',
    bg: 'rgba(59,130,246,0.12)',
    icon: <SyncOutlined spin />,
  },
  Succeeded: {
    label: 'Succeeded',
    color: '#22c55e',
    bg: 'rgba(34,197,94,0.12)',
    icon: <CheckCircleOutlined />,
  },
  Failed: {
    label: 'Failed',
    color: '#ef4444',
    bg: 'rgba(239,68,68,0.12)',
    icon: <ExclamationCircleOutlined />,
  },
  In_Queue: {
    label: 'In Queue',
    color: '#f59e0b',
    bg: 'rgba(245,158,11,0.12)',
    icon: <HourglassOutlined />,
  },
  Cancelled: {
    label: 'Cancelled',
    color: '#6b7280',
    bg: 'rgba(107,114,128,0.12)',
    icon: <ClockCircleOutlined />,
  },
  Timed_Out: {
    label: 'Timed Out',
    color: '#8b5cf6',
    bg: 'rgba(139,92,246,0.12)',
    icon: <ExclamationCircleOutlined />,
  },
};

export const LANG_STYLES: Record<MVLanguageEnum, React.CSSProperties> = {
  'Spark Python': { background: 'rgba(52,152,219,0.15)', color: '#3498db' },
  'Spark SQL/PostgreSQL': {
    background: 'rgba(220,53,69,0.12)',
    color: '#dc3545',
  },
  'Spark R': { background: 'rgba(255,193,7,0.15)', color: '#b8860b' },
  'Spark Scala': { background: 'rgba(118,74,188,0.15)', color: '#7646bd' },
};

export const DEFAULT_LANG_STYLE: React.CSSProperties = {
  background: 'rgba(107,114,128,0.12)',
  color: '#6b7280',
};

export function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1_000);
  const parts: string[] = [];
  if (h) parts.push(`${h}h`);
  if (m) parts.push(`${m}m`);
  parts.push(`${s}s`);
  return parts.join(' ');
}

export function formatMemory(mb: number | null): string {
  if (mb == null) return '—';
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`;
}

export function CopyableValue({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <span className="job-details-sidebar__copyable">
      <span className="job-details-sidebar__mono">{value}</span>
      <Tooltip title={copied ? 'Copied!' : 'Copy'}>
        <CopyOutlined
          onClick={copy}
          className={[
            'job-details-sidebar__copy-icon',
            copied && 'job-details-sidebar__copy-icon--copied',
          ]
            .filter(Boolean)
            .join(' ')}
        />
      </Tooltip>
    </span>
  );
}

export function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="job-details-sidebar__section">
      <p className="job-details-sidebar__section-title">{title}</p>
      <div className="job-details-sidebar__section-body">{children}</div>
    </div>
  );
}

export function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="job-details-sidebar__row">
      <span className="job-details-sidebar__row-label">{label}</span>
      <span className="job-details-sidebar__row-value">{children}</span>
    </div>
  );
}

// ─── JobCodeModal helpers ────────────────────────────────────────────────────

export const LANG_PILL_COLORS: Record<string, { bg: string; color: string }> = {
  [MVLanguageEnum['Spark Python']]: { bg: '#fef9c3', color: '#854d0e' },
  [MVLanguageEnum['Spark SQL/PostgreSQL']]: { bg: '#dbeafe', color: '#1e40af' },
  [MVLanguageEnum['Spark R']]: { bg: '#fce7f3', color: '#9d174d' },
  [MVLanguageEnum['Spark Scala']]: { bg: '#dcfce7', color: '#166534' },
};

export function getLangPillColor(mvLanguage: string | undefined) {
  return (
    LANG_PILL_COLORS[mvLanguage ?? ''] ?? { bg: '#f3f4f6', color: '#374151' }
  );
}

export function formatCode(raw: unknown): string {
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(`"${raw.replace(/^"|"$/g, '')}"`);
      return typeof parsed === 'string' ? parsed : raw;
    } catch {
      return raw.replace(/\\n/g, '\n').replace(/\\t/g, '\t');
    }
  }
  return JSON.stringify(raw, null, 2);
}

export function detectSyntaxLabel(code: string): string {
  if (/SELECT|FROM|WHERE|JOIN|GROUP BY|WITH/i.test(code)) return 'SQL';
  if (/def |import |print\(/i.test(code)) return 'Python';
  if (/val |def |object |case class /i.test(code)) return 'Scala';
  if (/<-|library\(|function\(/i.test(code)) return 'R';
  return 'Code';
}

const SQL_KEYWORDS_RE =
  /\b(SELECT|FROM|WHERE|JOIN|LEFT|RIGHT|INNER|OUTER|ON|GROUP BY|ORDER BY|HAVING|WITH|AS|AND|OR|NOT|IN|IS|NULL|DISTINCT|LIMIT|OFFSET|UNION|ALL|CASE|WHEN|THEN|ELSE|END|BY|DESC|ASC|SUM|AVG|COUNT|MAX|MIN|OVER|PARTITION|dense_rank|rank|ROW_NUMBER)\b/gi;

const SQL_SPLIT_RE =
  /(`[^`]*`|\b(?:SELECT|FROM|WHERE|JOIN|LEFT|RIGHT|INNER|OUTER|ON|GROUP\s+BY|ORDER\s+BY|HAVING|WITH|AS|AND|OR|NOT|IN|IS|NULL|DISTINCT|LIMIT|OFFSET|UNION|ALL|CASE|WHEN|THEN|ELSE|END|BY|DESC|ASC|SUM|AVG|COUNT|MAX|MIN|OVER|PARTITION|dense_rank|rank|ROW_NUMBER)\b)/gi;

export function highlightSQL(code: string): React.ReactNode {
  const segments = code.split(SQL_SPLIT_RE);
  return (
    <>
      {segments.map((seg, i) => {
        SQL_KEYWORDS_RE.lastIndex = 0;
        if (SQL_KEYWORDS_RE.test(seg)) {
          return (
            <span key={i} className="job-code__syntax-keyword">
              {seg}
            </span>
          );
        }
        if (/^`[^`]*`$/.test(seg)) {
          return (
            <span key={i} className="job-code__syntax-identifier">
              {seg}
            </span>
          );
        }
        return <span key={i}>{seg}</span>;
      })}
    </>
  );
}
